import { createFileRoute, Link } from "@tanstack/react-router";
import { seoMeta } from "@/lib/seo/head";
import { MODULES } from "@/lib/billing/modules";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Sparkles } from "lucide-react";

export const Route = createFileRoute("/moduli")({
  head: () => seoMeta({
    title: "Moduli di specializzazione legale — LexAIPro",
    description: "15 moduli AI specializzati per il diritto italiano: privacy, lavoro, tributario, societario, penale e altri. Prezzi da €9/mese, sconti fino al 25% in bundle.",
    path: "/moduli",
  }),
  component: ModuliPage,
});

const tierLabel: Record<string, string> = {
  premium: "Premium",
  standard: "Standard",
  beta: "Beta",
};

function ModuliPage() {
  return (
    <main className="mx-auto max-w-6xl px-4 py-16">
      <div className="mb-10 text-center">
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-3 py-1 text-xs">
          <Sparkles className="h-3 w-3" /> Configurabile
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl">
          Moduli di specializzazione
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          Attiva i moduli che ti servono. Ogni modulo aggiunge all'AI un system prompt dedicato,
          fonti normative pertinenti e un glossario tecnico per risposte affidabili nel tuo ambito.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg"><Link to="/signup">Provalo gratis</Link></Button>
          <Button asChild size="lg" variant="outline"><Link to="/">Vedi i prezzi</Link></Button>
        </div>
      </div>

      <div className="mb-8 rounded-xl border bg-muted/30 p-5 text-sm">
        <div className="mb-2 font-semibold">Sconti automatici</div>
        <ul className="grid gap-1 text-muted-foreground sm:grid-cols-3">
          <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" />−15% da 3 moduli</li>
          <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" />−25% da 5 moduli</li>
          <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" />−20% piano annuale</li>
        </ul>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {MODULES.map((m) => (
          <Card key={m.id} className="flex flex-col p-5">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold">{m.name}</h2>
              <Badge variant="outline" className="text-[10px] uppercase">{tierLabel[m.tier]}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{m.short}</p>
            <div className="my-4 font-display text-2xl font-bold">
              €{m.monthlyPrice}
              <span className="text-xs font-normal text-muted-foreground">/mese</span>
            </div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Fonti incluse
            </div>
            <ul className="mb-4 space-y-1 text-sm">
              {m.sources.map((s) => (
                <li key={s} className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />{s}</li>
              ))}
            </ul>
            <Button asChild variant="outline" size="sm" className="mt-auto">
              <Link to="/signup">Attiva modulo</Link>
            </Button>
          </Card>
        ))}
      </div>

      <div className="mt-16 rounded-2xl border bg-gradient-to-br from-accent/10 to-transparent p-8 text-center">
        <h2 className="font-display text-2xl font-bold">Tutti i moduli, un solo prezzo</h2>
        <p className="mt-2 text-muted-foreground">
          Con il piano <strong>Studio</strong> hai tutti i 15 moduli inclusi.
        </p>
        <Button asChild size="lg" className="mt-4"><Link to="/">Confronta i piani</Link></Button>
      </div>
    </main>
  );
}
